/**
 * 데이터 브라우저 정적 카탈로그
 *
 * /api/data에는 모듈/테이블을 조회할 엔드포인트가 없다(unauthorized).
 * 따라서 "무엇을 브라우징할 수 있는가"를 프론트가 정적으로 안다.
 * 백엔드가 테이블명을 알려주면 DATA_CATALOG에 한 항목 추가로 확장한다.
 */

export interface TableDef {
  /** 실제 테이블명 (API 경로에 사용). 예: 'posts' | 'comments' */
  name: string;
  /** 드롭다운 표시명. 예: '게시글' | '댓글' */
  label: string;
  /** related 조회 rowId로 쓸 컬럼명 (해당 행의 PK) */
  pk: string;
  /**
   * 이 테이블 행에서 이동 가능한 연관 테이블 (없으면 FK 버튼 없음).
   * columns 지정 시 연관 행 뷰에서 해당 컬럼만(지정 순서대로) 표시.
   */
  fk?: { table: string; label: string; columns?: string[] };
  /** 표(그리드)에서 감출 컬럼명. 행 상세 패널에는 여전히 전체 노출. */
  hidden?: string[];
  /** 표(그리드)에서 소수점 2자리로 반올림해 표시할 숫자 컬럼명. */
  round?: string[];
  /** 행 상세 패널에서도 제외할 컬럼명 (항상 비어있는 컬럼 등). */
  detailHidden?: string[];
  /** 컬럼명 → 설명(주석). 그리드 머리글 hover 시 툴팁으로 표시. */
  comments?: Record<string, string>;
}

/** kosown.td_mkt_stck_dcby_inf — 종목 일별 투자자 매매동향. 컬럼 주석(확정 정의). */
const TD_MKT_STCK_DCBY_COMMENTS: Record<string, string> = {
  // 기본정보
  bsdt: '기준일자',
  item_id: '종목ID(단축코드)',
  item_nm: '종목명',
  crt_stat: '생성상태',
  // 매매동향 — 보험
  insr_sel_qt: '보험 매도수량',
  insr_buy_qt: '보험 매수수량',
  insr_ntby_qt: '보험 순매수수량',
  // 매매동향 — 투신(투자신탁)
  trst_sel_qt: '투신(투자신탁) 매도수량',
  trst_buy_qt: '투신(투자신탁) 매수수량',
  trst_ntby_qt: '투신(투자신탁) 순매수수량',
};

/** kosown.tb_krx_new_pban — KRX 신규상장 공시 스냅샷. 컬럼 주석(백엔드 schema.sql 기준). */
const KRX_NEW_PBAN_COMMENTS: Record<string, string> = {
  pban_dt: '공시일자',
  item_id: '종목 ID',
  item_knm: '종목 한글명',
  pbl_bur_nm: '공표기관명',
  lstn_dv_cd: '상장구분코드',
  lstn_dt: '상장일자',
  ins_id: '입력자 ID',
  ins_dtt: '입력 일시',
  upd_id: '수정자 ID',
  upd_dtt: '수정 일시',
};

/** kosown.tb_kid_mn_etf_pban — ETF 공시(발행) 스냅샷. 컬럼 주석(백엔드 schema.sql 기준). */
const KID_MN_ETF_PBAN_COMMENTS: Record<string, string> = {
  pban_rpot_dv_cd:
    '공시보고서구분코드 [01:종목명변경 02:ETF변경등록·집합투자규약변경 03:ETF투자유의안내 04:ETF상장폐지사유발생]',
  pban_dt: '공시일자',
  item_id: '종목 ID',
  pban_hms: '공시 시분초',
  sot_isin: '단축 ISIN',
  item_nm: '종목명',
  subm_bur_nm: '제출기관명',
  pban_rfdt: '공시반영일자',
  pban_rson: '공시사유',
  ins_id: '등록자 ID',
  ins_dtt: '등록 일시',
  upd_id: '수정자 ID',
  upd_dtt: '수정 일시',
};

/** pblob.comp_earnings — 기업 실적 비정형 문서(docparser). 컬럼 주석(백엔드 CompEarningsStore 기준). */
const COMP_EARNINGS_COMMENTS: Record<string, string> = {
  doc_id: '문서 ID (원본 sha256 — 재수집해도 행이 늘지 않음)',
  source_url: '원본 문서 URL',
  title: '문서 제목',
  doc_date: '문서 일자',
  file_name: '원본 파일명',
  mime_type: 'MIME 타입',
  byte_size: '원본 크기(바이트)',
  page_count: '페이지 수',
  md_path: '변환 마크다운 경로 (문서 보기로 열람)',
  image_paths: '변환 이미지 경로 목록 (문서 보기로 열람)',
  convert_job_id: '변환 작업 ID',
  batch_id: '수집 배치 ID',
  source_meta: '조회 API 원본 레코드(JSON)',
  collected_at: '수집 시각',
};

export const DATA_CATALOG: Record<string, TableDef[]> = {
  naverdiscuss: [
    {
      name: 'posts',
      label: '게시글',
      pk: 'nid',
      fk: { table: 'comments', label: '댓글', columns: ['author', 'text'] },
      hidden: ['post_time'],
      detailHidden: ['selftext', 'innerhtml'],
    },
    { name: 'comments', label: '댓글', pk: 'id' },
  ],
  reddit: [
    {
      name: 'posts',
      label: '게시글',
      pk: 'post_id',
      fk: { table: 'comments', label: '댓글', columns: ['author', 'text'] },
      hidden: ['subreddit_id', 'author_id', 'author_icon'],
      round: ['upvote_ratio'],
      detailHidden: ['external_url', 'media_url'],
    },
    { name: 'comments', label: '댓글', pk: 'id' },
  ],
  kosown: [
    {
      name: 'td_mkt_stck_dcby_inf',
      label: 'td_mkt_stck_dcby_inf',
      pk: 'item_id',
      comments: TD_MKT_STCK_DCBY_COMMENTS,
    },
    {
      name: 'tb_krx_new_pban',
      label: 'tb_krx_new_pban',
      pk: 'item_id',
      comments: KRX_NEW_PBAN_COMMENTS,
    },
    {
      name: 'tb_etf_cmpf_inf',
      label: 'tb_etf_cmpf_inf',
      pk: 'item_id',
    },
    {
      name: 'tb_equity_daily_data',
      label: 'tb_equity_daily_data',
      pk: 'mst_id_seqn',
    },
    {
      name: 'tb_kid_mn_etf_pban',
      label: 'tb_kid_mn_etf_pban',
      pk: 'item_id',
      comments: KID_MN_ETF_PBAN_COMMENTS,
    },
  ],

  // 비정형 문서(docparser) 적재 스키마. md_path/image_paths는 파일 경로라
  // 그리드에서는 감추고, 행 상세의 "문서 보기"로 실제 문서를 연다.
  pblob: [
    {
      name: 'comp_earnings',
      label: '기업 실적 문서',
      pk: 'doc_id',
      hidden: ['md_path', 'image_paths', 'source_meta', 'convert_job_id', 'doc_id'],
      comments: COMP_EARNINGS_COMMENTS,
    },
  ],
};

export const DATA_MODULES: string[] = Object.keys(DATA_CATALOG);

export function getTables(module: string): TableDef[] {
  return DATA_CATALOG[module] ?? [];
}

export function getTableDef(module: string, table: string): TableDef | undefined {
  return getTables(module).find((t) => t.name === table);
}

export function firstTable(module: string): string {
  return getTables(module)[0]?.name ?? '';
}

/** 해당 모듈/테이블의 그리드에서 감출 컬럼명 목록 (없으면 빈 배열). */
export function getHiddenColumns(module: string, table: string): string[] {
  return getTableDef(module, table)?.hidden ?? [];
}

/** 해당 모듈/테이블의 그리드에서 소수점 2자리로 반올림해 표시할 컬럼명 목록 (없으면 빈 배열). */
export function getRoundColumns(module: string, table: string): string[] {
  return getTableDef(module, table)?.round ?? [];
}

/** 해당 모듈/테이블의 행 상세 패널에서도 제외할 컬럼명 목록 (없으면 빈 배열). */
export function getDetailHiddenColumns(module: string, table: string): string[] {
  return getTableDef(module, table)?.detailHidden ?? [];
}

/**
 * 연관 행(FK) 뷰에서 표시할 컬럼 화이트리스트 (지정 순서 유지).
 * 없으면 빈 배열 = 전체 컬럼 표시.
 */
export function getRelatedColumns(module: string, table: string): string[] {
  return getTableDef(module, table)?.fk?.columns ?? [];
}

/**
 * 컬럼명 → 설명(주석) 맵. 그리드 머리글 hover 툴팁에 사용.
 * 없으면 빈 객체 = 툴팁 없음.
 */
export function getColumnComments(module: string, table: string): Record<string, string> {
  return getTableDef(module, table)?.comments ?? {};
}
